<template lang="pug">
  div
    .d-flex.justify-space-between.align-center.pl-2.mb-1
      div
        .text-h5 文章管理
        .text-caption.grey--text 草稿不會出現在公開網站；發佈後立即生效
      v-btn(outlined color="blue" @click="newArticle")
        v-icon(left) mdi-plus
        | 新增文章
    v-divider
    v-alert.mt-4(v-if="loadError" type="error" outlined) {{ loadError }}
    v-row.mt-0
      v-col(cols="12" md="4")
        v-card(outlined)
          v-list(dense nav max-height="680" class="overflow-y-auto")
            v-list-item(v-if="!articles.length")
              v-list-item-content.grey--text 尚無文章
            v-list-item-group(v-model="selectedIndex" active-class="blue lighten-5 blue--text" @change="selectArticle")
              v-list-item(v-for="article in articles" :key="article.uid")
                v-list-item-content
                  v-list-item-title {{ article.title }}
                  v-list-item-subtitle {{ article.post_date }}
                v-list-item-action
                  v-chip(x-small :color="article.status === 'published' ? 'green' : 'grey'" dark) {{ article.status === 'published' ? '已發佈' : '草稿' }}
      v-col(cols="12" md="8")
        v-card.pa-5(outlined)
          .d-flex.align-center.mb-4
            .text-h6 {{ form.uid ? '編輯文章' : '新增文章' }}
            v-spacer
            v-btn(v-if="form.uid" text color="error" :disabled="saving" @click="deleteArticle") 刪除
          v-form(ref="articleForm" v-model="valid" @submit.prevent="saveArticle")
            v-text-field(v-model="form.title" label="文章標題" :counter="200" :rules="rules.title" outlined dense)
            v-textarea(v-model="form.summary" label="文章摘要（選填）" :counter="500" rows="2" auto-grow outlined dense)
            v-row
              v-col(cols="12" sm="6")
                v-text-field(v-model="form.post_date" type="date" label="發佈日期" :rules="rules.required" outlined dense)
              v-col(cols="12" sm="6")
                v-select(v-model="form.status" :items="statuses" label="狀態" outlined dense)
            v-text-field(v-model="form.tags" label="標籤（以逗號分隔）" hint="例如：生殖新知, 試管嬰兒" persistent-hint outlined dense)
            v-textarea.mt-3(v-model="form.content" label="文章內容" :rules="rules.required" rows="12" auto-grow outlined)
            v-text-field(v-model="form.video_url" label="影片連結（選填）" hint="貼上公開的 YouTube 或 Facebook 影片網址，文章內頁會直接顯示播放器" persistent-hint outlined dense)
            v-file-input.mt-3(v-model="coverFile" accept="image/jpeg,image/png,image/webp,image/gif" label="封面圖片（JPG、PNG、WEBP、GIF，最大 5MB）" prepend-icon="mdi-image" :rules="coverRules" outlined dense @change="coverChanged")
            v-img.mt-2(v-if="previewUrl" :src="previewUrl" max-height="360" contain class="grey lighten-4 rounded")
            .d-flex.justify-end.mt-5
              v-btn(type="submit" color="primary" :loading="saving" :disabled="!valid || saving") {{ form.uid ? '儲存變更' : '建立文章' }}
</template>

<script>
import axios from "axios";
import moment from "moment";
import { articleImageUrl, fetchArticles } from "@/services/articles";

const emptyForm = () => ({
  uid: null,
  title: "",
  summary: "",
  content: "",
  post_date: moment().format("YYYY-MM-DD"),
  tags: "",
  video_url: "",
  status: "draft",
  img_url: "",
});

export default {
  name: "Articles",

  data() {
    return {
      articles: [],
      selectedIndex: null,
      form: emptyForm(),
      coverFile: null,
      localPreviewUrl: null,
      valid: false,
      saving: false,
      loadError: "",
      statuses: [
        { text: "草稿", value: "draft" },
        { text: "已發佈", value: "published" },
      ],
      rules: {
        required: [(value) => Boolean(value) || "此欄位為必填"],
        title: [
          (value) => Boolean(value) || "請輸入文章標題",
          (value) => String(value || "").length <= 200 || "最多 200 字",
        ],
      },
    };
  },

  computed: {
    previewUrl() {
      return (
        this.localPreviewUrl ||
        (this.form.img_url ? articleImageUrl(this.form) : "")
      );
    },
    coverRules() {
      return [
        (file) =>
          Boolean(file || this.form.img_url) || "新增文章必須上傳封面圖片",
        (file) => !file || file.size <= 5 * 1024 * 1024 || "圖片不可超過 5MB",
      ];
    },
  },

  methods: {
    async loadArticles(selectUid) {
      try {
        this.loadError = "";
        this.articles = await fetchArticles({ admin: true });
        if (selectUid) {
          const index = this.articles.findIndex(
            (item) => item.uid === selectUid
          );
          if (index >= 0) {
            this.selectedIndex = index;
            this.selectArticle();
          }
        }
      } catch (err) {
        this.loadError =
          (err.response && err.response.data && err.response.data.message) ||
          "無法載入文章清單";
      }
    },
    selectArticle() {
      const article = this.articles[this.selectedIndex];
      if (!article) return;
      this.clearLocalPreview();
      this.coverFile = null;
      this.form = {
        ...article,
        summary: article.summary || "",
        tags: article.tags.join(", "),
        video_url: article.video_url || "",
      };
      this.$nextTick(() => {
        if (this.$refs.articleForm) this.$refs.articleForm.resetValidation();
      });
    },
    newArticle() {
      this.selectedIndex = null;
      this.clearLocalPreview();
      this.coverFile = null;
      this.form = emptyForm();
      this.$nextTick(() => {
        if (this.$refs.articleForm) this.$refs.articleForm.resetValidation();
      });
    },
    coverChanged(file) {
      this.clearLocalPreview();
      if (file) this.localPreviewUrl = URL.createObjectURL(file);
    },
    clearLocalPreview() {
      if (this.localPreviewUrl) URL.revokeObjectURL(this.localPreviewUrl);
      this.localPreviewUrl = null;
    },
    toFormData() {
      const data = new FormData();
      data.append("title", this.form.title);
      data.append("summary", this.form.summary || "");
      data.append("content", this.form.content);
      data.append("post_date", this.form.post_date);
      data.append("tags", this.form.tags || "");
      data.append("video_url", this.form.video_url || "");
      data.append("status", this.form.status);
      if (this.coverFile) data.append("file", this.coverFile);
      return data;
    },
    async saveArticle() {
      if (!this.$refs.articleForm.validate()) return;
      this.saving = true;
      try {
        const isUpdate = Boolean(this.form.uid);
        const response = isUpdate
          ? await axios.put(`articles/${this.form.uid}`, this.toFormData())
          : await axios.post("articles", this.toFormData());
        const uid = response.data.article.uid;
        await this.loadArticles(uid);
        this.$swal.fire({
          title: isUpdate ? "文章已更新" : "文章已建立",
          icon: "success",
          confirmButtonText: "關閉",
        });
      } catch (err) {
        this.$swal.fire({
          title: "儲存失敗",
          text:
            (err.response && err.response.data && err.response.data.message) ||
            "請稍後再試",
          icon: "error",
          confirmButtonText: "關閉",
        });
      } finally {
        this.saving = false;
      }
    },
    async deleteArticle() {
      const confirm = await this.$swal.fire({
        title: `確定刪除「${this.form.title}」？`,
        text: "刪除後無法復原",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "刪除",
        cancelButtonText: "取消",
      });
      if (!confirm.isConfirmed) return;
      this.saving = true;
      try {
        await axios.delete(`articles/${this.form.uid}`);
        this.newArticle();
        await this.loadArticles();
        this.$swal.fire({
          title: "文章已刪除",
          icon: "success",
          confirmButtonText: "關閉",
        });
      } catch (err) {
        this.$swal.fire({
          title: "刪除失敗",
          text:
            (err.response && err.response.data && err.response.data.message) ||
            "請稍後再試",
          icon: "error",
          confirmButtonText: "關閉",
        });
      } finally {
        this.saving = false;
      }
    },
  },

  created() {
    if (!this.$store.state.pass) this.$router.push({ name: "BackStage" });
  },
  mounted() {
    this.loadArticles();
  },
  beforeDestroy() {
    this.clearLocalPreview();
  },
};
</script>
